import { GroupCondition } from '@/api/enums';
import { equal, group, handleAxiosError, nested } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import { Campaign } from '@/client/campaigns';
import { MaterialType } from '@/client/courses';
import {
  ExportReportFormValues,
  ExportType,
  UsersReportType,
} from '@/client/reports';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getPhishingReportsExportColumns,
  getReportsExportColumns,
} from '@/utils/helpers/reports-export.helper';
import { AxiosError } from 'axios';
import { Field, Form, Formik } from 'formik';
import { Dialog, DialogProps } from 'primereact/dialog';
import { RadioButtonChangeEvent } from 'primereact/radiobutton';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 } from 'uuid';
import { FormikRadio } from '../form';
import { ExportContext } from './ReportMessage';

const StyledDialog = styled(Dialog)`
  width: 500px;
`;
type ReportsExportModalProps = {
  type: UsersReportType;
  visibleColumns: string[];
  filters?: FiltersType;
  sort?: [string, 'asc' | 'desc'] | null;
  campaign?: Campaign;
} & DialogProps;

export const ReportsExportModal: React.FC<ReportsExportModalProps> = ({
  type,
  visibleColumns,
  filters,
  sort,
  campaign,
  onHide,
  visible,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const [initialValues] = useState<ExportReportFormValues>({
    isAllData: true,
    areAllColumns: true,
  });

  const {
    setType,
    setExportKey,
    setIsDownloading,
    setNotificationParam,
    isConnected,
  } = useContext(ExportContext);

  const exportAwarenessReport = (
    account: Account,
    isAllData: boolean,
    areAllColumns: boolean,
  ) => {
    const awarenessExportKey = v4();

    const defaultFilter = group(GroupCondition.OR, [
      nested('course', [equal('type', MaterialType.COURSE)]),
      nested('course', [equal('type', MaterialType.CUSTOM_MATERIAL)]),
    ]);

    const exportFilters = filters
      ? [...filters, defaultFilter]
      : [defaultFilter];

    client.reports.exportUsersAwarenessReport(account.id, {
      filters: isAllData ? [defaultFilter] : exportFilters,
      exportKey: awarenessExportKey,
      sort: sort && sort?.length > 0 ? [sort.join(',')] : [],
      columns: areAllColumns
        ? undefined
        : getReportsExportColumns(visibleColumns),
    });

    setExportKey(awarenessExportKey);
  };

  const exportSummaryReport = (
    account: Account,
    isAllData: boolean,
    areAllColumns: boolean,
  ) => {
    const summaryExportKey = v4();

    client.reports.exportUsersSummaryReport(account.id, {
      filters: isAllData ? [] : filters,
      exportKey: summaryExportKey,
      sort: sort && sort?.length > 0 ? [sort.join(',')] : [],
      columns: areAllColumns
        ? undefined
        : getReportsExportColumns(visibleColumns),
    });

    setExportKey(summaryExportKey);
  };

  const exportPhishingReport = (
    exportCampaignId: string,
    isAllData: boolean,
    areAllColumns: boolean,
  ) => {
    const phishingExportKey = v4();

    client.campaigns.exportPhishingReport(exportCampaignId, {
      filters: isAllData ? [] : filters,
      exportKey: phishingExportKey,
      columns: areAllColumns
        ? undefined
        : getPhishingReportsExportColumns(visibleColumns),
    });

    setExportKey(phishingExportKey);
  };

  const exportReport = async ({
    isAllData,
    areAllColumns,
  }: ExportReportFormValues) => {
    onHide();

    if (campaign?.id || account) {
      setType(type as unknown as ExportType);
      setExportKey(undefined);
      setNotificationParam(campaign?.id || account?.id);
      setIsDownloading(true);

      // Wait for a notify connection to establish
      await new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (isConnected.current) {
            clearInterval(intervalId);
            resolve(true);
          }
        }, 100);
      });

      try {
        if (isConnected.current) {
          if (type === UsersReportType.AWARENESS && account) {
            exportAwarenessReport(account, isAllData, areAllColumns);
          } else if (type === UsersReportType.SUMMARY && account) {
            exportSummaryReport(account, isAllData, areAllColumns);
          } else if (type === UsersReportType.PHISHING && campaign?.id) {
            exportPhishingReport(campaign?.id, isAllData, areAllColumns);
          }
        }
      } catch (e) {
        setIsDownloading(false);
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }
  };

  return (
    <StyledDialog
      blockScroll
      visible={visible}
      header={<h1>{t('generic.export')}</h1>}
      onHide={onHide}
      draggable={false}
      data-testid="reports-export-modal"
    >
      <Formik initialValues={initialValues} onSubmit={exportReport}>
        {({ setFieldValue, values }) => (
          <Form>
            <label htmlFor="data">{t('reports.export.data')}</label>
            <FlexContainer
              id="data"
              gap={24}
              justify="flex-start"
              className="mt-1 mb-4 w-8"
            >
              <div className="field-radio flex-1" data-id="allData">
                <Field
                  id="allData"
                  type="radio"
                  name="allData"
                  value={true}
                  label={t('reports.export.allData')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('isAllData', e.value)
                  }
                  checked={!!values.isAllData}
                  component={FormikRadio}
                />
              </div>
              <div className="field-radio flex-1" data-id="filteredData">
                <Field
                  id="filteredData"
                  type="radio"
                  name="filteredData"
                  value={false}
                  label={t('reports.export.filteredData')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('isAllData', e.value)
                  }
                  checked={!values.isAllData}
                  component={FormikRadio}
                />
              </div>
            </FlexContainer>

            <label htmlFor="columns">{t('reports.export.columns')}</label>
            <FlexContainer
              id="columns"
              gap={24}
              justify="flex-start"
              className="mt-1 mb-4 w-8"
            >
              <div className="field-radio flex-1" data-id="allColumns">
                <Field
                  id="allColumns"
                  name="allColumns"
                  type="radio"
                  value={true}
                  label={t('reports.export.allColumns')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('areAllColumns', e.value)
                  }
                  checked={!!values.areAllColumns}
                  component={FormikRadio}
                />
              </div>
              <div className="field-radio flex-1" data-id="visibleColumns">
                <Field
                  id="visibleColumns"
                  name="visibleColumns"
                  type="radio"
                  value={false}
                  label={t('reports.export.visibleColumns')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('areAllColumns', e.value)
                  }
                  checked={!values.areAllColumns}
                  component={FormikRadio}
                />
              </div>
            </FlexContainer>

            <FlexContainer justify="flex-end" className="mt-5">
              <AppButton
                label={t('button.cancel')}
                severity="secondary"
                type="outlined"
                onClick={onHide}
                className="mr-3"
                data-testid="reports-export-cancel-form"
              />
              <AppButton
                icon="pi pi-download"
                label={t('generic.export')}
                isSubmit
                data-testid="reports-export-submit-form"
              />
            </FlexContainer>
          </Form>
        )}
      </Formik>
    </StyledDialog>
  );
};
